/**
 * useFootballDashboardFilters - Centralizes the translation of rail filter
 * UI changes into normalized filter state consumed by useFootballFixtures.
 *
 * Shared by both mobile and desktop dashboards so that identical user
 * interactions produce identical normalized filter values, eliminating
 * any divergence in how filters are interpreted.
 *
 * Responsibilities:
 *  1. Own the canonical selectedDate and selectedLeague state.
 *  2. Initialize and wire the useFootballRailFilters hook (UI state).
 *  3. Normalize every rail filter change consistently.
 *  4. Expose both the normalized values (for data fetching) and the
 *     full rail-filters API (for UI binding).
 */

import { useState, useCallback, useMemo } from "react";
import { rtdbFootballService as FootballService } from "../rtdb";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";
import useFootballRailFilters from "../../../../components/dashboards/mobile/hooks/useFootballRailFilters";

/**
 * @returns {Object} Combined filter state and rail-filter UI API
 */
const useFootballDashboardFilters = () => {
  const todayDate = useMemo(() => FootballService.getTodayDate(), []);

  // Canonical filter values consumed by useFootballFixtures
  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [selectedLeague, setSelectedLeague] = useState("");

  /**
   * Single normalization function applied to every rail filter change.
   * Both mobile and desktop go through this exact path, so the result
   * is guaranteed to be identical for the same rail filter inputs.
   */
  const handleRailFiltersChange = useCallback(
    (nextFilters) => {
      const normalizedDate = ensureISODate(nextFilters.date, todayDate);
      const normalizedLeague =
        nextFilters.league && nextFilters.league !== "all"
          ? String(nextFilters.league?.id ?? nextFilters.league)
          : "";

      setSelectedDate(normalizedDate);
      setSelectedLeague(normalizedLeague);
    },
    [todayDate]
  );

  // Rail filters hook -- owns UI state (tabs, sheets, chips)
  const railFilters = useFootballRailFilters({
    initialDate: todayDate,
    onFiltersChange: handleRailFiltersChange,
  });

  return {
    // Normalized filter values (use these for data fetching)
    selectedDate,
    selectedLeague,
    todayDate,

    // Rail filter UI state and handlers (use these for rendering)
    railFilters: railFilters.filters,
    railActiveTab: railFilters.activeTab,
    railHasActiveFilters: railFilters.hasActiveFilters,
    railPrimaryTabs: railFilters.primaryTabs,
    railFilterObjects: railFilters.filterObjects,
    handleRailTabChange: railFilters.handleTabChange,
    handleFilterClick: railFilters.handleFilterClick,
    updateRailFilter: railFilters.updateFilter,
    resetRailFilters: railFilters.resetFilters,

    // Sheet / modal visibility
    showDateSheet: railFilters.showDateSheet,
    setShowDateSheet: railFilters.setShowDateSheet,
    showLeagueSheet: railFilters.showLeagueSheet,
    setShowLeagueSheet: railFilters.setShowLeagueSheet,
    showMarketSheet: railFilters.showMarketSheet,
    setShowMarketSheet: railFilters.setShowMarketSheet,
    showSortSheet: railFilters.showSortSheet,
    setShowSortSheet: railFilters.setShowSortSheet,
  };
};

export default useFootballDashboardFilters;
